import { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import {
	fetchConversionRates,
	mergeRatesIntoPharmacyPOS,
	persistPharmacyPOS,
} from "../utils/conversionRates";
import {
	DEFAULT_PHARMACY_DATE_FORMAT_TOKEN,
	normalizeStoredPharmacyDateFormat,
	pickPharmacyDateFormatRaw,
} from "../utils/dateFormat";
import {
	DEFAULT_PHARMACY_TIME_FORMAT,
	normalizeStoredPharmacyTimeFormat,
	pickPharmacyTimeFormatRaw,
} from "../utils/timeFormat";

const pharmacyContext = createContext();

/** Logged-in pharmacy from localStorage. Safe when storage is missing or JSON is invalid. */
export const readStoredPharmacyPOS = () => {
	try {
		if (typeof localStorage === "undefined") return null;
		const saved = localStorage.getItem("currentPharmacyPOS");
		if (!saved) return null;
		const parsed = JSON.parse(saved);
		return parsed && typeof parsed === "object" ? parsed : null;
	} catch {
		return null;
	}
};

/**
 * Ensure top-level `date_format` / `time_format` exist on a pharmacy POS object.
 * Reads nested currency / login fields, previous storage, then falls back to
 * DD MMM YYYY and 12h. Display formats are never sent to APIs by this helper.
 */
export const buildPharmacyWithDisplayFormats = (pharmacyPOS, extraSources = {}) => {
	const base =
		pharmacyPOS && typeof pharmacyPOS === "object" ? { ...pharmacyPOS } : {};
	const previous = readStoredPharmacyPOS();

	const rawDate =
		pickPharmacyDateFormatRaw(base) ??
		pickPharmacyDateFormatRaw(extraSources) ??
		pickPharmacyDateFormatRaw(previous);
	const rawTime =
		pickPharmacyTimeFormatRaw(base) ??
		pickPharmacyTimeFormatRaw(extraSources) ??
		pickPharmacyTimeFormatRaw(previous);

	return {
		...base,
		date_format:
			normalizeStoredPharmacyDateFormat(rawDate) ||
			DEFAULT_PHARMACY_DATE_FORMAT_TOKEN,
		time_format:
			normalizeStoredPharmacyTimeFormat(rawTime) ||
			DEFAULT_PHARMACY_TIME_FORMAT,
	};
};

/** Persist pharmacy POS to localStorage (+ optional React state) with display formats. */
export const writePharmacyPOS = (pharmacyPOS, setPharmacyPOS, extraSources = {}) => {
	const next = buildPharmacyWithDisplayFormats(pharmacyPOS, extraSources);
	if (typeof localStorage !== "undefined") {
		localStorage.setItem("currentPharmacyPOS", JSON.stringify(next));
	}
	if (typeof setPharmacyPOS === "function") {
		setPharmacyPOS(next);
	}
	return next;
};

/**
 * Patch only `date_format` / `time_format` onto the existing `currentPharmacyPOS`.
 * All other stored properties stay unchanged.
 */
export const patchPharmacyDisplayFormats = (formats = {}, setPharmacyPOS) => {
	const existing = readStoredPharmacyPOS();
	const base = existing && typeof existing === "object" ? { ...existing } : {};
	const nextBase = { ...base };
	if (Object.prototype.hasOwnProperty.call(formats, "date_format")) {
		nextBase.date_format = formats.date_format;
	}
	if (Object.prototype.hasOwnProperty.call(formats, "time_format")) {
		nextBase.time_format = formats.time_format;
	}
	return writePharmacyPOS(nextBase, setPharmacyPOS, {
		date_format: formats.date_format ?? base.date_format,
		time_format: formats.time_format ?? base.time_format,
	});
};

export const PharmacyProvider = (props) => {
	const [pharmacyPOS, setPharmacyPOS] = useState(() => {
		const stored = readStoredPharmacyPOS();
		return stored ? buildPharmacyWithDisplayFormats(stored) : null;
	});

	const pharmacyPOSRef = useRef(pharmacyPOS);

	useEffect(() => {
		pharmacyPOSRef.current = pharmacyPOS;
	}, [pharmacyPOS]);

	const refreshConversionRates = useCallback(async () => {
		const result = await fetchConversionRates(pharmacyPOSRef.current);

		if (result.success) {
			const updated = mergeRatesIntoPharmacyPOS(
				pharmacyPOSRef.current,
				result.rates,
				result.baseCurrency
			);
			const withFormats = buildPharmacyWithDisplayFormats(updated);
			persistPharmacyPOS(withFormats);
			pharmacyPOSRef.current = withFormats;
			setPharmacyPOS(withFormats);
		}

		return result;
	}, []);

	return (
		<pharmacyContext.Provider
			value={{ ...props, pharmacyPOS, setPharmacyPOS, refreshConversionRates }}
		>
			{props.children}
		</pharmacyContext.Provider>
	);
};

export const usePharmacy = () => useContext(pharmacyContext);
